import {SceneNodes} from "@cm/template-nodes"
import {ThreeObject, getThreeObjectPart, mathIsEqual, setThreeObjectPart, updateTransform} from "@template-editor/helpers/three-object"
import {Three as THREE} from "@cm/material-nodes/three"
import {ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {anyDifference, arrayDifferent, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {ThreeAnnotationObject} from "@app/template-editor/helpers/three-annotation"
import {ThreeMesh} from "@app/template-editor/helpers/three-mesh"
import {projectCurvePointsToObject} from "@app/template-editor/helpers/three-utils"
import {configThreeHelperObjectLayers} from "@app/template-editor/helpers/helper-objects"
import {Subscription} from "rxjs"

// the curve as it lies on the mesh, which only the mesh's geometry in the three scene can tell
export function projectMeshCurveOntoMesh(threeSceneManagerService: ThreeSceneManagerService, curve: SceneNodes.MeshCurveControl, transform: THREE.Matrix4) {
    const {reified} = curve.completeCurveData
    if (!reified) return null

    //We need to update the world matrix of the scene to get the correct hit points even before the first render
    threeSceneManagerService.getSceneReference().updateMatrixWorld()

    const threeMeshObject = threeSceneManagerService.getThreeObject(curve.mesh.id)
    if (!threeMeshObject || !(threeMeshObject instanceof ThreeMesh)) throw new Error(`Mesh object with id ${curve.mesh.id} not found`)

    const segmentLength = 0.1
    const queryRange = 2 * segmentLength
    return projectCurvePointsToObject(reified, transform, queryRange, threeMeshObject.getRenderObject())
}

export class ThreeMeshCurveControl extends ThreeObject<SceneNodes.MeshCurveControl> {
    protected override renderObject = new THREE.Group()

    protected curveGeometry = new THREE.BufferGeometry()
    protected curveOccludedMaterial = new THREE.LineBasicMaterial({color: 0xff0000, depthTest: false})
    protected curveMaterial = new THREE.LineBasicMaterial({color: 0x0000ff})
    protected occludedCurve = new THREE.Line(this.curveGeometry, this.curveOccludedMaterial)
    protected curve = new THREE.Line(this.curveGeometry, this.curveMaterial)
    protected curveObject = new THREE.Group()
    protected controlPoints = new THREE.Group()

    protected hitRaysGeometry = new THREE.BufferGeometry()
    protected hitRaysMaterial = new THREE.LineBasicMaterial({color: 0xffffff, depthTest: false})
    protected hitRays = new THREE.LineSegments(this.hitRaysGeometry, this.hitRaysMaterial)

    protected hitCurveGeometry = new THREE.BufferGeometry()
    protected hitCurveMaterial = new THREE.LineBasicMaterial({color: 0x00ff00, depthTest: false})
    protected hitCurve = new THREE.LineSegments(this.hitCurveGeometry, this.hitCurveMaterial)
    protected subscription: Subscription

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)

        setThreeObjectPart(this.getRenderObject(), this)

        this.renderObject.add(this.controlPoints)

        this.curveObject.add(this.occludedCurve)
        this.curveObject.add(this.curve)
        this.curveObject.renderOrder = 1
        this.renderObject.add(this.curveObject)

        this.renderObject.add(this.hitRays)

        this.renderObject.renderOrder = 1
        this.renderObject.add(this.hitCurve)

        configThreeHelperObjectLayers(this.getRenderObject())

        this.subscription = threeSceneManagerService.showUnselectedCurves$.subscribe(() => {
            this.onSelectionChange(this.selected)
            this.threeSceneManagerService.redraw()
        })
    }

    override setup(sceneNode: SceneNodes.MeshCurveControl) {
        const updateGeometry = (oldGeometry: THREE.BufferGeometry, points: Float32Array | null) => {
            oldGeometry.dispose()
            const newGeometry = new THREE.BufferGeometry()
            newGeometry.setAttribute("position", new THREE.BufferAttribute(points ?? new Float32Array(), 3))
            newGeometry.computeBoundingSphere()
            return newGeometry
        }

        return anyDifference([
            arrayDifferent(
                sceneNode.controlPoints,
                this.parameters?.controlPoints,
                (a, b) => mathIsEqual(a.position, b.position),
                (controlPoints) => {
                    for (const [i, controlPoint] of controlPoints.entries()) {
                        const previousPoint = this.parameters?.controlPoints.at(i)
                        if (!previousPoint) {
                            const annotation = new ThreeAnnotationObject(
                                this.threeSceneManagerService,
                                {
                                    sceneNode,
                                    part: `controlPoint${i}`,
                                },
                                false,
                            )
                            annotation.position.copy(controlPoint.position)
                            annotation.updateLabels(`${i + 1}`, "")
                            this.controlPoints.add(annotation)
                            setThreeObjectPart(annotation, this, `controlPoint${i}`)
                        } else {
                            const annotation = this.getControlPoint(i)
                            annotation.updateSceneNode(sceneNode)
                            if (!mathIsEqual(controlPoint.position, previousPoint.position)) {
                                annotation.position.copy(controlPoint.position)
                            }
                        }
                    }

                    for (let i = controlPoints.length; i < (this.parameters?.controlPoints.length ?? 0); i++) {
                        const annotation = this.getControlPoint(i)
                        this.controlPoints.remove(annotation)
                        annotation.dispose(true)
                    }
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["completeCurveData"],
                (a, b) => a.reified?.points === b.reified?.points,
                ({completeCurveData}) => {
                    const points = completeCurveData.reified?.points ?? null
                    this.curveGeometry = updateGeometry(this.curveGeometry, points)
                    this.occludedCurve.geometry = this.curveGeometry
                    this.curve.geometry = this.curveGeometry
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.renderObject)
                },
            ),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["completeCurveData", "mesh"],
                (a, b) => {
                    if (a === null || b === null) return a === b
                    else if (SceneNodes.isSceneNode(a)) {
                        if (SceneNodes.isSceneNode(b)) return a.id === b.id
                        else return false
                    } else if (SceneNodes.isSceneNode(b)) return false
                    return a.reified?.points === b.reified?.points && a.reified?.normals === b.reified?.normals && a.reified?.tangents === b.reified?.tangents
                },
                ({completeCurveData}) => {
                    const projection = projectMeshCurveOntoMesh(this.threeSceneManagerService, sceneNode, this.renderObject.matrix)

                    if (completeCurveData.reified && projection) {
                        const {points} = completeCurveData.reified

                        const {hitPoints, valid} = projection

                        const numValid = valid.reduce((acc, value) => acc + (value ? 1 : 0), 0)
                        const hitRays = new Float32Array(numValid * 2 * 3)

                        let hitRaysIndex = 0
                        for (let i = 0; i < valid.length; i++) {
                            if (valid[i]) {
                                hitRays[hitRaysIndex++] = points[i * 3]
                                hitRays[hitRaysIndex++] = points[i * 3 + 1]
                                hitRays[hitRaysIndex++] = points[i * 3 + 2]

                                const hitPoint = hitPoints[i]

                                hitRays[hitRaysIndex++] = hitPoint.x
                                hitRays[hitRaysIndex++] = hitPoint.y
                                hitRays[hitRaysIndex++] = hitPoint.z
                            }
                        }

                        const numConsecutiveValid = valid.reduce((acc, value, index) => acc + (value && index > 0 && valid[index - 1] ? 1 : 0), 0)
                        const hitCurve = new Float32Array(numConsecutiveValid * 2 * 3)

                        let hitCurveIndex = 0
                        for (let i = 0; i < valid.length; i++) {
                            if (valid[i] && i > 0 && valid[i - 1]) {
                                const hitPoint = hitPoints[i]
                                const previousHitPoint = hitPoints[i - 1]

                                hitCurve[hitCurveIndex++] = hitPoint.x
                                hitCurve[hitCurveIndex++] = hitPoint.y
                                hitCurve[hitCurveIndex++] = hitPoint.z

                                hitCurve[hitCurveIndex++] = previousHitPoint.x
                                hitCurve[hitCurveIndex++] = previousHitPoint.y
                                hitCurve[hitCurveIndex++] = previousHitPoint.z
                            }
                        }

                        this.hitRaysGeometry = updateGeometry(this.hitRaysGeometry, hitRays)
                        this.hitCurveGeometry = updateGeometry(this.hitCurveGeometry, hitCurve)
                    } else {
                        this.hitRaysGeometry = updateGeometry(this.hitRaysGeometry, null)
                        this.hitCurveGeometry = updateGeometry(this.hitCurveGeometry, null)
                    }

                    this.hitRays.geometry = this.hitRaysGeometry
                    this.hitCurve.geometry = this.hitCurveGeometry
                },
            ),
        ])
    }

    getControlPoint(index: number) {
        for (const child of this.controlPoints.children) {
            if (child instanceof ThreeAnnotationObject) {
                const threeObjectPart = getThreeObjectPart(child)

                if (threeObjectPart && threeObjectPart.part === `controlPoint${index}`) return child
            }
        }

        throw new Error(`Control point with index ${index} not found`)
    }

    override dispose() {
        for (const child of this.controlPoints.children) {
            if (child instanceof ThreeAnnotationObject) child.dispose(true)
        }

        this.curveGeometry.dispose()
        this.curveOccludedMaterial.dispose()
        this.curveMaterial.dispose()

        this.hitRaysGeometry.dispose()
        this.hitRaysMaterial.dispose()

        this.hitCurveGeometry.dispose()
        this.hitCurveMaterial.dispose()

        this.subscription.unsubscribe()
    }

    override onSelectionChange(selected: boolean) {
        this.controlPoints.visible = selected
        this.curveObject.visible = selected || this.threeSceneManagerService.$showUnselectedCurves()
        this.hitRays.visible = selected
        this.hitCurve.visible = selected
    }
}
